import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiUser, FiMessageSquare } = FiIcons;

function Contact() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    legalMatter: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message. We will contact you within 24 hours.');
  };

  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Office Address',
      details: [
        '15th Floor, Sandton City Office Tower',
        'Corner 5th Street & Alice Lane',
        'Sandton, 2196, Johannesburg, Gauteng'
      ]
    },
    {
      icon: FiPhone,
      title: 'Phone Numbers',
      details: [
        'Main: +27 11 123 4567',
        'Reception: +27 11 123 4568',
        'Emergency: +27 82 123 4567'
      ]
    },
    {
      icon: FiMail,
      title: 'Email Addresses',
      details: [
        'General: law@gnt.co.za',
        'Consultations: consult@gnt.co.za',
        'Reception: reception@gnt.co.za'
      ]
    },
    {
      icon: FiClock,
      title: 'Office Hours',
      details: [
        'Monday - Friday: 08:00 - 17:00',
        'Saturday: 09:00 - 13:00 (By appointment)',
        'Sunday: Closed'
      ]
    }
  ];

  const legalMatterTypes = [
    'Corporate Law',
    'Employment Law',
    'Commercial Litigation',
    'Contract Dispute',
    'Business Advisory',
    'Regulatory Compliance',
    'Other'
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              Contact <span className="text-gold-600">Our Team</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-3xl mx-auto">
              Ready to discuss your legal needs? Get in touch with our experienced attorneys for a consultation. We're here to help you navigate your legal challenges with confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-white p-8 rounded-lg shadow-lg"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-6">
                  <SafeIcon icon={info.icon} className="w-8 h-8" />
                </div>
                <h3 className="font-playfair font-bold text-xl text-navy-800 mb-4">
                  {info.title}
                </h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="font-opensans text-gray-600 text-sm">
                      {detail}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={formRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-playfair font-bold text-3xl text-navy-800 mb-6">
                Schedule Your Consultation
              </h2>
              <p className="font-opensans text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours to schedule your consultation.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-opensans font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-opensans font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-opensans font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiPhone} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-opensans font-medium text-gray-700 mb-2">
                      Legal Matter Type *
                    </label>
                    <select
                      name="legalMatter"
                      required
                      value={formData.legalMatter}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                    >
                      <option value="">Select legal matter type</option>
                      {legalMatterTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block font-opensans font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                    placeholder="Brief subject of your inquiry"
                  />
                </div>
                <div>
                  <label className="block font-opensans font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans resize-none"
                      placeholder="Please describe your legal matter and any specific questions you have..."
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiSend} className="w-5 h-5" />
                  <span>Send Message</span>
                </button>
              </form>
            </motion.div>

            {/* Map and Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={formInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-4">
                  Visit Our Office
                </h3>
                <p className="font-opensans text-gray-600 mb-6">
                  Located in the heart of Sandton, our office is easily accessible by public transport and offers convenient parking facilities.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h4 className="font-playfair font-bold text-xl text-navy-800 mb-4">
                  What to Expect
                </h4>
                <ul className="space-y-3 font-opensans text-gray-700">
                  <li className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-gold-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Initial consultation to understand your legal needs</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-gold-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Clear explanation of legal options and strategies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-gold-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Transparent discussion of fees and timelines</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-gold-600 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Personalized legal advice for your situation</span>
                  </li>
                </ul>
              </div>
              <div className="bg-navy-800 text-white p-8 rounded-lg">
                <h4 className="font-playfair font-bold text-xl mb-4">
                  Emergency Legal Services
                </h4>
                <p className="font-opensans text-gray-200 mb-4">
                  For urgent legal matters that require immediate attention, please call our emergency line:
                </p>
                <p className="font-opensans text-gold-600 font-semibold text-lg">
                  +27 82 123 4567
                </p>
                <p className="font-opensans text-gray-300 text-sm mt-2">
                  Available 24/7 for emergency legal consultations
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;